import { createSlice, current } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

/**
 * NcApprovalSlice — Redux state for the Non-Chargeable (NC) approval flow.
 *
 * from @dotpe/rista-pos-common can be reused verbatim.
 *
 * State shape per order (keyed by `mid` inside the array):
 *   isApprovalRequired  true  → approval not yet sent; "Request Approval" CTA visible
 *   isRequested         true  → approval request sent to backend; "Check Status" CTA visible
 *   isApproved          true  → manager approved; order can be closed as NC
 *   mid                       → order ID this approval belongs to
 *   approvalRequestId         → backend-assigned ID returned after save
 *   currentStatus             → latest status from backend polling
 *   approvalData              → raw approvalRequests[0] from backend
 */

export type NcApprovalType = {
  isApproved: boolean;
  mid: string;
  isApprovalRequired: boolean;
  isRequested: boolean;
  approvalRequestId?: string;
  currentStatus?: string;
  approvalData?: any;
  requestType?: string;
};

export interface NcApprovalState {
  ncApproval: NcApprovalType[];
}

const initialState: NcApprovalState = {
  ncApproval: [],
};

/**
 * 
 * Finds an existing entry by `mid` and merges the payload, or appends it.
 */

function upsertNcApproval(list: NcApprovalType[], payload: NcApprovalType): NcApprovalType[] {
  const index = list.findIndex((entry) => entry.mid === payload.mid);
  if (index === -1) {
    return [payload];
  }
  const existingApproval = list[index];
  if (existingApproval?.requestType && !payload?.requestType) {
    return list;
  }
  return [payload];
}

export const ncApprovalSlice = createSlice({
  name: "ncApproval",
  initialState,
  reducers: {
    /**
     * Add or update NC approval data for a given order (mid).
     * Call with { isApprovalRequired: true } to show the "Request Approval" CTA.
     * Call with { isRequested: true, approvalRequestId } after the save API responds.
     * Call with { isApproved: true } / reset payload after manager approves/rejects.
     */
    addNcApprovalData: (state, action: PayloadAction<NcApprovalType>) => {
      state.ncApproval = upsertNcApproval(state.ncApproval, action.payload);
    },

    /**
     * Full reset — called on logout or when a new session starts.
     */
    resetNcApproval: () => initialState,
  },
});

export const {
  addNcApprovalData,
  resetNcApproval,
} = ncApprovalSlice.actions;

export default ncApprovalSlice.reducer;
